import { authenticatedAxios } from "./apiClient";
import { getAuthHeader } from "./auth";
import { fetchBillDetail as fetchBillDetailFromUtils, fetchBills } from "./utils";

/**
 * API contract checkpoint (Phase 0)
 * ---------------------------------
 * This file normalizes campaign endpoint payloads so web UI doesn't drift from
 * backend/mobile field semantics.
 *
 * GET /api/campaigns/ -> Array<CampaignListItemRaw>
 * {
 *   id, name, description, status, target_bill_id, target_bill_type,
 *   target_bill_number, target_bill_congress, target_bill_db_id,
 *   target_bioguide_ids, target_states, start_datetime, end_datetime, timezone,
 *   max_calls, calls_used, cost_per_call, total_cost, remaining_calls,
 *   amount_paid, is_active_now, created_at, updated_at
 * }
 *
 * GET /api/campaigns/:id/ -> CampaignDetailRaw
 * (same shape as list item + stripe_payment_intent_id)
 *
 * GET /api/campaigns/:id/stats/ -> CampaignStatsRaw
 * {
 *   campaign_id, campaign_name, total_calls, calls_used, max_calls,
 *   remaining_calls, budget_used_percent, outcome_breakdown, status_breakdown,
 *   average_duration_seconds, calls_by_day
 * }
 *
 * POST /api/campaigns/ -> { id, name, status, total_cost, message }
 * PUT /api/campaigns/:id/ -> { id, name?, status, message }
 */

const toNumber = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const toArray = (value) => (Array.isArray(value) ? value : []);

const normalizeCampaign = (raw = {}) => ({
  id: raw.id || "",
  name: raw.name || "",
  description: raw.description || "",
  email_subject: raw.email_subject || "",
  email_body: raw.email_body || "",
  status: raw.status || "draft",
  verified_calling_enabled: Boolean(raw.verified_calling_enabled),
  action_script: raw.action_script || "",
  rep_source: raw.rep_source || "custom_list",
  custom_targets: toArray(raw.custom_targets),
  additional_links: toArray(raw.additional_links),
  bill_mode: raw.bill_mode || null,
  bill: raw.bill || null,
  action_completions: toNumber(raw.call_completions ?? raw.action_completions, 0),
  call_completions: toNumber(raw.call_completions ?? raw.action_completions, 0),
  call_completions_verified: toNumber(raw.call_completions_verified, 0),
  email_completions: toNumber(raw.email_completions, 0),
  email_completions_verified: toNumber(raw.email_completions_verified, 0),
  target_bill_id: raw.target_bill_id || null,
  target_bill_type: raw.target_bill_type || null,
  target_bill_number: raw.target_bill_number || null,
  target_bill_congress: raw.target_bill_congress ?? null,
  target_bill_db_id: raw.target_bill_db_id ?? null,
  target_bioguide_ids: toArray(raw.target_bioguide_ids),
  target_states: toArray(raw.target_states),
  start_datetime: raw.start_datetime || null,
  end_datetime: raw.end_datetime || null,
  timezone: raw.timezone || "America/New_York",
  schedule_locked: Boolean(raw.schedule_locked),
  max_calls: toNumber(raw.max_calls, 0),
  calls_used: toNumber(raw.calls_used, 0),
  cost_per_call: toNumber(raw.cost_per_call, 0),
  total_cost: toNumber(raw.total_cost, 0),
  remaining_calls: raw.remaining_calls == null ? null : toNumber(raw.remaining_calls, 0),
  amount_paid: toNumber(raw.amount_paid, 0),
  stripe_payment_intent_id: raw.stripe_payment_intent_id || null,
  is_active_now: Boolean(raw.is_active_now),
  share_path: raw.share_path || (raw.id ? `/take-action/${raw.id}` : null),
  organizer_name: raw.organizer_name || "",
  organizer_id: raw.organizer_id || null,
  organizer_is_verified: Boolean(raw.organizer_is_verified),
  created_at: raw.created_at || null,
  updated_at: raw.updated_at || null,
});

const normalizeCampaignStats = (raw = {}) => ({
  campaign_id: raw.campaign_id || "",
  campaign_name: raw.campaign_name || "",
  total_calls: toNumber(raw.total_calls, 0),
  calls_used: toNumber(raw.calls_used, 0),
  max_calls: toNumber(raw.max_calls, 0),
  remaining_calls: toNumber(raw.remaining_calls, 0),
  budget_used_percent: toNumber(raw.budget_used_percent, 0),
  outcome_breakdown: raw.outcome_breakdown || {},
  status_breakdown: raw.status_breakdown || {},
  average_duration_seconds: toNumber(raw.average_duration_seconds, 0),
  calls_by_day: toArray(raw.calls_by_day),
});

export async function fetchCampaigns(statusFilter = null) {
  const params = {};
  if (statusFilter) params.status = statusFilter;
  const response = await authenticatedAxios("get", "/api/campaigns/", null, { params });
  const items = Array.isArray(response.data) ? response.data : [];
  return items.map(normalizeCampaign);
}

export async function fetchCampaign(campaignId) {
  const response = await authenticatedAxios("get", `/api/campaigns/${campaignId}/`);
  return normalizeCampaign(response.data || {});
}

export async function createCampaign(campaignData) {
  const response = await authenticatedAxios("post", "/api/campaigns/", campaignData);
  const createdId = response.data?.id || response.data?.campaign_id || "";
  return {
    id: createdId,
    name: response.data?.name || "",
    status: response.data?.status || "draft",
    verified_calling_enabled: Boolean(response.data?.verified_calling_enabled),
    total_cost: toNumber(response.data?.total_cost, 0),
    share_path: response.data?.share_path || (createdId ? `/take-action/${createdId}` : null),
    message: response.data?.message || "",
  };
}

export async function updateCampaign(campaignId, updates) {
  const response = await authenticatedAxios("put", `/api/campaigns/${campaignId}/`, updates);
  return response.data || {};
}

export async function deleteCampaign(campaignId) {
  const response = await authenticatedAxios("delete", `/api/campaigns/${campaignId}/`);
  return response.data || {};
}

export async function fetchCampaignStats(campaignId) {
  const response = await authenticatedAxios("get", `/api/campaigns/${campaignId}/stats/`);
  return normalizeCampaignStats(response.data || {});
}

export async function createCampaignPayment(campaignId) {
  const response = await authenticatedAxios("post", `/api/campaigns/${campaignId}/pay/`, {});
  return response.data || {};
}

export async function confirmCampaignPayment(campaignId) {
  const response = await authenticatedAxios("post", `/api/campaigns/${campaignId}/confirm-payment/`, {});
  return response.data || {};
}

export async function createCampaignCheckout(campaignId) {
  const response = await authenticatedAxios("post", `/api/campaigns/${campaignId}/checkout/`, {});
  return response.data || {};
}

export async function searchCampaignBills(query) {
  if (!query || query.trim().length === 0) return { bills: [] };
  const filterModel = {
    items: [{ field: "congress", value: 119, operator: "equals" }],
  };
  const result = await fetchBills(1, 20, query.trim(), [], "", "", "", filterModel, "-status_progression");
  return { bills: result?.bills || [] };
}

export async function fetchCampaignBillDetail(billId) {
  return fetchBillDetailFromUtils(billId);
}

export async function prefillCampaignTargets(stateCode, district = "") {
  const params = stateCode ? { state: stateCode } : {};
  if (district) params.district = district;
  const response = await authenticatedAxios("get", "/api/campaigns/prefill-targets/", null, {
    params,
  });
  return response.data || { targets: [], note: "" };
}

export function getCampaignBillShortLabel(type, number) {
  const t = (type || "").toLowerCase();
  const num = number ? ` ${number}` : "";
  if (t === "hr") return `H.R.${num}`;
  if (t === "s") return `S.${num}`;
  if (t === "hjres") return `H.J.Res.${num}`;
  if (t === "sjres") return `S.J.Res.${num}`;
  if (t === "hconres") return `H.Con.Res.${num}`;
  if (t === "sconres") return `S.Con.Res.${num}`;
  return (type || "").toUpperCase() + num;
}

export function getCampaignBillTypeLabel(type, number, congress) {
  const t = (type || "").toLowerCase();
  const num = number ? ` ${number}` : "";
  const congressSuffix = congress ? ` — ${congress}th Congress` : "";
  if (t === "hr") return `H.R.${num} (House)${congressSuffix}`;
  if (t === "s") return `S.${num} (Senate)${congressSuffix}`;
  if (t === "hjres") return `H.J.Res.${num} (House)${congressSuffix}`;
  if (t === "sjres") return `S.J.Res.${num} (Senate)${congressSuffix}`;
  if (t === "hconres") return `H.Con.Res.${num} (House)${congressSuffix}`;
  if (t === "sconres") return `S.Con.Res.${num} (Senate)${congressSuffix}`;
  return (type || "").toUpperCase() + num + congressSuffix;
}

/** Short bill label for campaign list cards and search. */
export function getCampaignListBillLabel(campaign) {
  if (!campaign) return "";
  if (campaign.bill_mode === "external") {
    return (campaign.bill?.link_name || campaign.bill?.link_url || "").trim();
  }
  if (campaign.target_bill_type && campaign.target_bill_number) {
    const suffix = campaign.target_bill_congress ? ` (${campaign.target_bill_congress}th)` : "";
    return `${getCampaignBillShortLabel(campaign.target_bill_type, campaign.target_bill_number)}${suffix}`;
  }
  return "";
}

/** CivixThread bill detail URL for in-app bills; external URL for external bill mode. */
export function resolveCampaignBillUrl({
  billMode = "in_app",
  billDbId = null,
  externalBillUrl = "",
  origin = "",
} = {}) {
  if (billMode === "external") {
    const url = String(externalBillUrl || "").trim();
    return /^https?:\/\//i.test(url) ? url : "";
  }
  const id = billDbId || null;
  if (!id) return "";
  const base =
    origin ||
    (typeof window !== "undefined" ? window.location.origin : "https://www.civixthread.com");
  return `${base}/bills/${id}`;
}

function formatCampaignTargetLines(targets = []) {
  return (Array.isArray(targets) ? targets : [])
    .filter((t) => {
      const name = (t?.name || "").trim();
      const phone = (t?.phone || "").trim();
      const url = (t?.url || "").trim();
      return name && (phone || url);
    })
    .map((t) => {
      const name = (t.name || "").trim();
      const title = (t.title || "").trim();
      const phone = (t.phone || "").trim();
      const url = (t.url || "").trim();
      const label = title ? `${name} (${title})` : name;
      const lines = [`- ${label}`];
      if (phone) lines.push(`  Phone: ${phone}`);
      if (url) lines.push(`  Website: ${url}`);
      return lines.join("\n");
    })
    .join("\n\n");
}

/** Default supporter email draft seeded from Details-step fields. */
export function buildDefaultCampaignEmail({ name = "", description = "", actionScript = "" } = {}) {
  const campaignName = (name || "").trim() || "Campaign";
  const lines = ["Hi,", ""];
  if ((description || "").trim()) {
    lines.push("{{description}}", "");
  }
  lines.push(
    "Please take a few minutes to support this campaign.",
    "",
    "{{campaign_name}}",
    "Take action: {{take_action_url}}",
    "",
    "Bill: {{bill_line}}",
    "Bill link: {{bill_url}}",
    "",
    "What to say:",
    "{{script}}",
    "",
    "Helpful links:",
    "{{related_links}}",
    "",
    "Websites:",
    "{{targets}}"
  );
  return {
    subject: `Take action: ${campaignName}`,
    body: lines.join("\n"),
  };
}

/** Merge email template tokens with campaign content for preview/copy. */
export function composeCampaignEmailPreview({
  emailSubject = "",
  emailBody = "",
  name = "",
  description = "",
  actionScript = "",
  billLine = "",
  billUrl = "",
  relatedLinks = [],
  targets = [],
  shareUrl = "",
} = {}) {
  const linkLines = (Array.isArray(relatedLinks) ? relatedLinks : [])
    .filter((link) => link?.name && link?.url)
    .map((link) => `- ${link.name}: ${link.url}`)
    .join("\n");

  const targetLines = formatCampaignTargetLines(targets);
  const billUrlLine = (billUrl || "").trim();

  const replaceTokens = (text) =>
    String(text || "")
      .replaceAll("{{campaign_name}}", (name || "").trim() || "Campaign")
      .replaceAll("{{description}}", (description || "").trim())
      .replaceAll("{{take_action_url}}", shareUrl || "")
      .replaceAll("{{bill_line}}", (billLine || "").trim() || "No bill selected")
      .replaceAll("{{bill_url}}", billUrlLine || "No bill link available")
      .replaceAll("{{script}}", (actionScript || "").trim() || "Use the talking points on the take-action page.")
      .replaceAll("{{related_links}}", linkLines || "No additional links")
      .replaceAll("{{targets}}", targetLines || "No call targets listed");

  return {
    subject: replaceTokens(emailSubject),
    body: replaceTokens(emailBody),
  };
}

const MAILTO_MAX_LENGTH = 2048;

/** Open the default mail client with a draft; truncates body if the mailto URL is too long. */
export function openCampaignEmailDraft({ subject = "", body = "" } = {}) {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return { opened: false, truncated: false };
  }

  const subjectText = String(subject || "");
  let bodyText = String(body || "");
  const suffix = "\n\n…";
  let truncated = false;

  const buildHref = (subj, bod) =>
    `mailto:?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(bod)}`;

  let href = buildHref(subjectText, bodyText);
  if (href.length > MAILTO_MAX_LENGTH && bodyText.length > 0) {
    truncated = true;
    let trimmed = bodyText;
    while (trimmed.length > 0 && buildHref(subjectText, trimmed + suffix).length > MAILTO_MAX_LENGTH) {
      trimmed = trimmed.slice(0, Math.max(0, trimmed.length - 200));
    }
    bodyText = trimmed.length < String(body || "").length ? `${trimmed}${suffix}` : trimmed;
    href = buildHref(subjectText, bodyText);
  }

  if (href.length > MAILTO_MAX_LENGTH) {
    href = buildHref(subjectText, "");
    truncated = true;
  }

  const link = document.createElement("a");
  link.href = href;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  return { opened: true, truncated };
}

const escapeHtml = (str) =>
  String(str || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");

/**
 * Detects "title" lines in a composed email body so they can be rendered/copied
 * with bold emphasis: the campaign name on its own line, or a short "Label:" prefix
 * (e.g. "Bill:", "What to say:", "Helpful links:").
 * Returns null when the line isn't a title, otherwise { label, rest, isName }.
 */
export function parseEmailPreviewLine(line, campaignName = "") {
  const text = String(line || "");
  const trimmedName = (campaignName || "").trim();
  if (trimmedName && text.trim() === trimmedName) {
    return { label: text, rest: "", isName: true, isPhone: false };
  }
  const phoneMatch = text.match(/^(\s*Phone:\s*)(.+)$/);
  if (phoneMatch) {
    return { label: phoneMatch[1], rest: phoneMatch[2], isName: false, isPhone: true };
  }
  const match = text.match(/^([A-Za-z][A-Za-z ]{0,30}:)(\s*)(.*)$/);
  if (match) {
    return { label: match[1], rest: match[2] + match[3], isName: false, isPhone: false };
  }
  return null;
}

/** Rich-text (HTML) version of the composed email, for clipboard copy with bold titles. */
export function buildEmailPreviewHtml({ subject = "", body = "", name = "" } = {}) {
  const bodyHtml = String(body || "")
    .split("\n")
    .map((line) => {
      const parsed = parseEmailPreviewLine(line, name);
      if (!parsed) return escapeHtml(line) || "&nbsp;";
      const labelHtml = escapeHtml(parsed.label);
      const restHtml = escapeHtml(parsed.rest);
      const label = parsed.isName
        ? `<strong style="font-size:16px;">${labelHtml}</strong>`
        : `<strong>${labelHtml}</strong>`;
      const rest = parsed.isPhone
        ? `<strong style="font-size:18px;">${restHtml}</strong>`
        : restHtml;
      return `${label}${rest}`;
    })
    .join("<br>");
  return (
    `<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.5;color:#0f172a;">` +
    `<p style="margin:0 0 10px;"><strong>Subject:</strong> ${escapeHtml(subject)}</p>` +
    `<p style="margin:0;">${bodyHtml}</p>` +
    `</div>`
  );
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

/** Extract a user-facing message from DRF or campaign API error payloads. */
export function parseCampaignApiError(payload, fallback) {
  if (!payload || typeof payload !== "object") return fallback;
  if (payload.code === "district_lookup_unavailable") {
    if (typeof payload.detail === "string" && payload.detail.trim()) {
      return payload.detail.trim();
    }
  }
  if (typeof payload.error === "string" && payload.error.trim()) return payload.error.trim();
  if (typeof payload.message === "string" && payload.message.trim()) return payload.message.trim();
  if (typeof payload.detail === "string" && payload.detail.trim()) return payload.detail.trim();
  if (Array.isArray(payload.detail)) {
    const parts = payload.detail
      .map((entry) => {
        if (typeof entry === "string") return entry;
        if (entry && typeof entry === "object") return entry.message || entry.msg || "";
        return "";
      })
      .filter(Boolean);
    if (parts.length) return parts.join(" ");
  }
  return fallback;
}

export const TAKE_ACTION_UNAVAILABLE_TITLES = {
  not_found: "Campaign not found",
  draft: "Not published yet",
  pending_payment: "Not live yet",
  paused: "Campaign paused",
  cancelled: "Campaign cancelled",
  expired: "Campaign ended",
  scheduled: "Not open yet",
  unavailable: "Campaign unavailable",
};

export function getTakeActionUnavailableTitle(reason) {
  return TAKE_ACTION_UNAVAILABLE_TITLES[reason] || TAKE_ACTION_UNAVAILABLE_TITLES.unavailable;
}

/** Public take-action campaign detail (works logged-out; sends auth when available). */
export async function fetchPublicActionCampaign(campaignId) {
  const headers = { ...(await getAuthHeader()) };
  const res = await fetch(`${API_BASE_URL}/api/action-campaigns/${campaignId}/`, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const message = parseCampaignApiError(err, "We could not load this take-action page.");
    throw Object.assign(new Error(message), {
      status: res.status,
      reason: err.reason || (res.status === 404 ? "not_found" : "unavailable"),
      data: err,
    });
  }
  return res.json();
}

/** Self-reported call or email completion on a public take-action page. */
export async function completePublicActionCampaign(campaignId, actionType = "call") {
  const headers = { "Content-Type": "application/json" };
  try {
    const authHeaders = await getAuthHeader();
    Object.assign(headers, authHeaders);
  } catch {
    // Anonymous completions are allowed; auth header is optional for verified splits.
  }

  const res = await fetch(`${API_BASE_URL}/api/action-campaigns/${campaignId}/complete/`, {
    method: "POST",
    headers,
    body: JSON.stringify({ action_type: actionType }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const fallback = res.status === 429
      ? "You have reached the limit for marking actions. Please try again later."
      : actionType === "email"
        ? "Could not record your message right now. Please try again."
        : "Could not record your call right now. Please try again.";
    const message = parseCampaignApiError(err, fallback);
    throw Object.assign(new Error(message), { status: res.status, reason: err.reason, data: err });
  }
  return res.json();
}

/** Public address → House + senators lookup. */
export async function fetchPublicRepsByAddress({ street_1, city, state, zip_code, zipcode }) {
  const zip = (zip_code || zipcode || "").trim();
  const res = await fetch(`${API_BASE_URL}/api/public/reps-by-address/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ street_1, city, state, zip_code: zip }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const fallback = err.code === "district_lookup_unavailable"
      ? (err.detail || "We use the U.S. Census address service to find your congressional district. It's temporarily unavailable — please try again in a moment.")
      : res.status === 429
      ? "Too many address lookups. Please wait a few minutes and try again."
      : "Could not find representatives for that address.";
    throw Object.assign(new Error(parseCampaignApiError(err, fallback)), {
      status: res.status,
      data: err,
    });
  }
  return res.json();
}
